import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Loader2, RefreshCw, ArrowRight, FileCheck2 } from 'lucide-react';

const FIELD_LABELS = {
  description: 'Description',
  part_description: 'Description (PDF)',
  customer_part_no: 'Customer P/N',
  part_internal_no: 'Internal P/N',
  hs_code: 'HS Code',
  net_weight_grams: 'Net Wt (g)',
  packing_qty: 'Packing Qty (pcs/box)',
  box_size: 'Box Size',
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === '') return <span className="italic text-slate-400">empty</span>;
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
};

/**
 * Confirmation dialog for "Refresh from Part Master".
 * Receives a per-item changes report from the backend dry-run and asks the user
 * to confirm before applying. Hides duplicate `part_description` rows that mirror
 * `description` to reduce noise.
 */
export function PartSyncDiffDialog({ open, onOpenChange, changes, onConfirm, applying, docLabel = 'document' }) {
  // Hide redundant `part_description` diff rows when the same value is already
  // shown under `description` (they are mirrored server-side).
  const cleanedChanges = (changes || []).map((line) => {
    const cleaned = { ...line.changes };
    if (cleaned.description && cleaned.part_description
        && cleaned.description.to === cleaned.part_description.to) {
      delete cleaned.part_description;
    }
    return { ...line, changes: cleaned };
  });

  const totalLines = cleanedChanges.length;
  const totalFieldChanges = cleanedChanges.reduce(
    (sum, l) => sum + Object.keys(l.changes).length, 0
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-emerald-600" />
            Refresh from Part Master — Preview Changes
          </DialogTitle>
          <DialogDescription>
            {totalLines === 0 ? (
              <>This {docLabel} is already in sync with Part Master. Nothing to update.</>
            ) : (
              <>
                <strong>{totalLines}</strong> line item{totalLines === 1 ? '' : 's'} will be updated
                ({totalFieldChanges} field change{totalFieldChanges === 1 ? '' : 's'} in total).
                Review the changes below before applying.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-3" data-testid="part-sync-diff-list">
          {cleanedChanges.map((line, idx) => (
            <div key={line.item_id || idx} className="border rounded-lg p-3 bg-slate-50/60">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="font-mono text-xs">
                  Line {idx + 1}
                </Badge>
                <span className="font-mono text-sm font-semibold">
                  {line.part_internal_no || '—'}
                </span>
              </div>
              <div className="space-y-1.5">
                {Object.entries(line.changes).map(([field, diff]) => (
                  <div key={field} className="grid grid-cols-[140px_1fr] gap-3 text-sm items-start">
                    <span className="text-slate-500 font-medium pt-0.5">
                      {FIELD_LABELS[field] || field}:
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 line-through decoration-red-300">
                        {formatVal(diff.from)}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 font-medium">
                        {formatVal(diff.to)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="pt-3 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}
            data-testid="part-sync-cancel-btn">
            Cancel
          </Button>
          {totalLines > 0 && (
            <Button onClick={onConfirm} disabled={applying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="part-sync-confirm-btn">
              {applying ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileCheck2 className="mr-2 h-4 w-4" />
              )}
              {applying ? 'Applying…' : `Apply ${totalLines} change${totalLines === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
